import { z } from 'zod';
import { localizedStringSchema, optionalLocalizedStringSchema } from './i18n.js';
/**
 * Stable, URL-safe identifier. Derived from the English name on creation but
 * never auto-updated afterwards — the published JSON keys off these, so
 * renaming a brand must not break the mobile app's deep links or caches.
 */
export const slugSchema = z
    .string()
    .trim()
    .min(2)
    .max(64)
    .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'Use lowercase letters, numbers and single hyphens');
export function slugify(input) {
    return input
        .normalize('NFKD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 64);
}
/**
 * Publication gate for a single item. `draft` items are invisible to the mobile
 * app even after a global publish, which lets editors stage next season's models
 * inside the live catalog.
 */
/** A URL, or an empty string when the editor has not supplied one. */
const urlOrBlank = () => z
    .string()
    .trim()
    .max(500)
    .refine((v) => v === '' || /^https?:\/\//i.test(v), 'Must be a http(s) URL')
    .default('');
export const statusSchema = z.enum(['draft', 'live']);
export const CURRENCIES = ['BHD', 'SAR', 'AED', 'USD'];
export const currencySchema = z.enum(CURRENCIES).default('BHD');
/**
 * Reference to an object in the assets bucket. Only the S3 key is persisted —
 * absolute URLs are built at render time from the CDN base, so the CDN domain
 * can change without rewriting stored content.
 */
export const assetRefSchema = z.object({
    key: z.string().trim().min(1).max(512),
    contentType: z.string().trim().max(128).optional(),
    width: z.number().int().positive().max(20000).optional(),
    height: z.number().int().positive().max(20000).optional(),
    bytes: z.number().int().nonnegative().optional(),
    alt: optionalLocalizedStringSchema.optional(),
    /** Resized WebP renditions produced by the image processor, narrowest first. */
    renditions: z
        .array(z.object({
        key: z.string().trim().min(1).max(512),
        width: z.number().int().positive().max(20000),
        height: z.number().int().positive().max(20000).optional(),
        contentType: z.string().trim().max(128).default('image/webp'),
    }))
        .max(10)
        .default([]),
});
/** Money is stored as a number plus currency so the app can format and filter. */
export const priceSchema = z.object({
    amount: z.number().nonnegative().max(100_000_000),
    currency: currencySchema,
    /** Optional editor override, e.g. "Price on request". Wins over `amount` for display. */
    display: optionalLocalizedStringSchema.optional(),
});
export const emiSchema = z.object({
    monthly: z.number().nonnegative().max(1_000_000),
    currency: currencySchema,
    months: z.number().int().positive().max(120).optional(),
    /** Annual percentage rate, e.g. 3.5 */
    apr: z.number().nonnegative().max(100).optional(),
    display: optionalLocalizedStringSchema.optional(),
});
export const uspSchema = z.object({
    id: z.string().trim().min(1).max(64),
    text: localizedStringSchema,
    icon: assetRefSchema.optional(),
});
/** Six-digit hex, e.g. `#1A1A1A`. Normalised to lowercase on parse. */
export const hexColorSchema = z
    .string()
    .trim()
    .regex(/^#[0-9a-fA-F]{6}$/, 'Use a six-digit hex colour such as #1a1a1a')
    .transform((v) => v.toLowerCase());
/**
 * A paint/trim option on a model.
 *
 * `swatch` is what the app paints its colour chips with, and is normally
 * eye-droppered out of `exteriorImage` in the admin UI so the chip matches the
 * photography rather than a guess at the marketing name. Exterior and interior
 * shots are separate slots on the same entry, so "Night Black" carries both its
 * bodywork and its cabin photo.
 */
export const colorSchema = z.object({
    id: z.string().trim().min(1).max(64),
    name: localizedStringSchema,
    swatch: hexColorSchema,
    exteriorImage: assetRefSchema.optional(),
    interiorImage: assetRefSchema.optional(),
    sortOrder: z.number().int().min(0).max(10_000).default(0),
});
/**
 * The content block shared by models and variants — identical field set, so it
 * is defined once and reused.
 */
const vehicleContentShape = {
    name: localizedStringSchema,
    intro: optionalLocalizedStringSchema.optional(),
    description: optionalLocalizedStringSchema.optional(),
    tacticalImage: assetRefSchema.optional(),
    iconFront: assetRefSchema.optional(),
    iconSide: assetRefSchema.optional(),
    usps: z.array(uspSchema).max(24).default([]),
    startingPrice: priceSchema.optional(),
    startingEmi: emiSchema.optional(),
    sortOrder: z.number().int().min(0).max(100_000).default(0),
    status: statusSchema.default('draft'),
};
/**
 * Fields the legacy `VehicleData.json` feed carries that are not editorial copy:
 * classification, the "featured" flag and the outbound links the app deep-links
 * to. They exist so `/mobapp/v1.json` can reproduce that feed key-for-key.
 */
export const marketingSchema = z.object({
    /** Legacy `type`: Passenger or Commercial. */
    vehicleType: z.enum(['Passenger', 'Commercial']).default('Passenger'),
    /** Legacy `category`, e.g. SUV, Sedan, Pickup. Free text — the feed never enumerated it. */
    category: z.string().trim().max(64).default(''),
    /** Legacy `featured`, serialised as "Yes"/"No". */
    featured: z.boolean().default(false),
    /** Legacy `test_drive_availability`, serialised as "show"/"hide". */
    testDriveAvailable: z.boolean().default(true),
    externalLink: urlOrBlank(),
    /** Legacy `request_for_qoute` — the misspelling is preserved on output, not here. */
    quoteUrl: urlOrBlank(),
    testDriveUrl: urlOrBlank(),
    /**
     * Legacy `nid`, and the key this model appears under in the feed's `models`
     * map. Preserved from the source so existing clients keep resolving the same
     * ids; blank means the publisher falls back to position.
     */
    feedId: z.string().trim().max(32).default(''),
});
/**
 * Colours hang off the model rather than the variant: paint availability is a
 * model-level property in the source data and in how Motorcity sells, and
 * duplicating it per trim would guarantee the two drift apart.
 */
const colorsShape = { colors: z.array(colorSchema).max(40).default([]) };
/** Fields the API accepts on write. Ids and audit fields are server-controlled. */
export const brandInputSchema = z.object({
    name: localizedStringSchema,
    /**
     * The key this brand appears under in the legacy feed — `chery` and
     * `King Long` are spelled exactly that way there. Blank falls back to the
     * English name.
     */
    feedKey: z.string().trim().max(64).default(''),
    description: optionalLocalizedStringSchema.optional(),
    logo: assetRefSchema.optional(),
    coverImage: assetRefSchema.optional(),
    sortOrder: z.number().int().min(0).max(100_000).default(0),
    status: statusSchema.default('draft'),
});
export const modelInputSchema = z.object({
    ...vehicleContentShape,
    ...colorsShape,
    marketing: marketingSchema.default({}),
});
export const variantInputSchema = z.object(vehicleContentShape);
/** Creation wrappers: the client proposes the slug so it can be reviewed before it becomes permanent. */
export const createBrandSchema = brandInputSchema.extend({ brandId: slugSchema });
export const createModelSchema = modelInputSchema.extend({ modelId: slugSchema });
export const createVariantSchema = variantInputSchema.extend({ variantId: slugSchema });
/** Audit metadata stamped by the API on every write. */
export const auditSchema = z.object({
    createdAt: z.string().datetime(),
    createdBy: z.string(),
    updatedAt: z.string().datetime(),
    updatedBy: z.string(),
});
export const brandSchema = brandInputSchema.merge(auditSchema).extend({ brandId: slugSchema });
export const modelSchema = modelInputSchema
    .merge(auditSchema)
    .extend({ brandId: slugSchema, modelId: slugSchema });
export const variantSchema = variantInputSchema
    .merge(auditSchema)
    .extend({ brandId: slugSchema, modelId: slugSchema, variantId: slugSchema });
/** Explicit reorder payload — avoids N round-trips when an editor drags a list. */
export const reorderSchema = z.object({
    ids: z.array(slugSchema).min(1).max(500),
});
export const uploadUrlRequestSchema = z.object({
    fileName: z.string().trim().min(1).max(255),
    contentType: z.enum([
        'image/jpeg',
        'image/png',
        'image/webp',
        'image/avif',
        'image/svg+xml',
        'application/pdf',
    ]),
    contentLength: z
        .number()
        .int()
        .positive()
        .max(25 * 1024 * 1024, 'Assets must be 25 MB or smaller'),
    /** Logical folder, e.g. "brands/mg/models". Keeps the bucket browsable. */
    prefix: z
        .string()
        .trim()
        .max(128)
        .regex(/^[a-z0-9/_-]*$/i, 'Invalid prefix')
        .default(''),
});
export const inviteUserSchema = z.object({
    email: z.string().trim().email().max(254),
    name: z.string().trim().min(1).max(120),
    role: z.enum(['admin', 'editor']).default('editor'),
});
export const publishRequestSchema = z.object({
    note: z.string().trim().max(500).default(''),
});
//# sourceMappingURL=schema.js.map